/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import {
    object, arrayOf, shape, string,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { useUIDSeed } from 'react-uid';
import {
    Grid, Button, Typography,
} from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import { Link, useHistory } from 'react-router-dom';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import { connect } from 'react-redux';
import mbpLogger from 'mbp-logger';
import { GRAPHQL_ENV } from '../../../gql';
import GraphqlBreadcrumbs from '../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import { getLogo } from '../../../../state/ducks/Common/Common-Selectors';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import ResponsiveImage from '../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import getRecipeCategories from './getRecipeCategories';
import { handleOnClick } from '../../ContentTypeComponents/utils/pathUtils';
// import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

const styles = ({ palette, typography, breakpoints }) => ({
    recipeContentWrapper: {
        borderBottom: `1px solid ${palette.common.grey || '#aeaeae'}`,
        padding: '35px',
        maxWidth: '85%',
        margin: '0 auto',

    },
    recipeImg: {
        width: '230px',
        display: 'block',
    },
    recipeName: {
        fontSize: '24px',
        fontWeight: '400',
        marginTop: '10px',
        color: palette.primary.main || '#2f2f2f',
    },
    recipeShortDescription: {
        '& em': {
            fontStyle: 'normal',
            lineHeight: '23px',
            color: palette.primary[600] || '#828282',
            fontSize: '18px',
            paddingTop: '10px',
            '& a': {
                color: palette.primary[600] || '#828282',
            },
        },
    },
    viewBtnWrapper: {
        alignSelf: 'center',
        textAlign: 'center',
    },
    viewBtn: {
        fontSize: '18px',
        fontWeight: '700',
        background: palette.cms?.recipeViewBtnBg || '#50627B',
        color: palette.common.white || '#fff',
        padding: '6px 50px',
        '&:hover': {
            background: palette.cms?.recipeViewBtnBg || '#50627B',
        },
    },
    arrowRightIcon: {
        fontSize: '26px',
    },
    categoryHeaderTitle: {
        textAlign: 'center',
        fontFamily: (typography.getFontFamily || (() => { }))('Crimson') || 'crimson_textregular',
        fontSize: '26px',
        letterSpacing: '1.2px',
        color: palette.primary.main || '#2f2f2f',
        fontWeight: '700',
        margin: '25px 40px',
    },
    categoryDiscriptionWrap: {
        alignSelf: 'center',
        borderLeft: `1px solid ${palette.common.black || '#000'}`,
        padding: '0 80px',
    },
    categoryDescription: {
        padding: '10px 0',
    },
    recipeCatListWrap: {
        margin: '0 auto',
        padding: '15px',
        fontSize: '16px',
        textAlign: 'center',
        justifyContent: 'center',
    },
    recipeCatList: {
        display: 'inline-flex',
        alignItems: 'center',
    },
    recipeCat: {
        borderRight: `1px solid ${palette.common.black || '#000'}`,
        padding: '2px 17px 0px 17px',
        cursor: 'pointer',
        color: palette.primary.main || '#2f2f2f',
        textDecoration: 'underline',
        textDecorationColor: '#bababa',
        height: 'fit-content',
        [breakpoints.down('sm')]: {
            padding: '2px 12px 0px 12px',
        },
        '&:last-child': {
            border: 'none',
        },
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    recipeCatListRoot: {
        background: palette.colorNeutral20 || '#f5f5f5',
        borderTop: `1px solid ${palette.colorDisabled || '#ccc'}`,
        borderBottom: `1px solid ${palette.colorDisabled || '#ccc'}`,
        maxWidth: '85%',
        margin: '0 auto',
        [breakpoints.down('sm')]: {
            maxWidth: '100%',
        },
    },
    recipeCatListTitle: {
        fontWeight: '600',
        fontSize: '1em',
    },
    breadCrumb: {
        padding: '10px',
    },
    recipeCatCont: {
        margin: '0 auto',
        textDecoration: 'none',
        display: 'block',
        width: '100%',
        cursor: 'pointer',
        color: palette.primary[600] || '#828282',
        fontSize: '18px',
        '&:last-child': {
            border: 'none',
        },
        '& p a': {
            color: palette.primary[600] || '#828282',
        },
    },
    error: {
        display: 'block',
        margin: '50px auto',
        textAlign: 'center',
    },
});
const RecipeCategoryPage = ({
    classes, page, recipes, recipeName, brandLogo, seo, brand,
}) => {
    const seed = useUIDSeed();
    const history = useHistory();
    const breadCrumb = [{
        title: 'Home',
        href: '/',
    }];
    const recipeCategoryList = getRecipeCategories(brand, GRAPHQL_ENV) || [];
    if (!recipeCategoryList) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: '',
            component: 'RecipeCategoryPage.js',
            message: `Warning: There are no recipes currently\n  associated with this category - ${page?.path}. \n  \n Make sure the category spelling on each \n  recipe matches "${page?.path.charAt(0).toUpperCase()}"`,
        });
    }

    recipeCategoryList.forEach((cat) => {
        if (cat.name === recipeName && cat.link === page?.path) {
            const data = {
                title: `Gourmet ${cat.name} Recipes | Harry & David`,
                href: cat.link,
            };
            breadCrumb.push(data);
        }
    });

    return (
        <>
            <GraphqlSeoHeadTags seoData={seo} />
            <div className={classes.breadCrumb}>
                <GraphqlBreadcrumbs breadCrumbArray={breadCrumb} />
            </div>
            <Grid container>
                <Grid item md={4} lg={4}>
                    <h1 className={classes.categoryHeaderTitle}>{recipeName} Recipes</h1>
                </Grid>
                <Grid item md={8} lg={8} className={classes.categoryDiscriptionWrap}>
                    <Typography className={classes.categoryDescription}>Create an exceptional gourmet meal with these mouthwatering recipes.</Typography>
                </Grid>
            </Grid>
            <div className={classes.recipeCatListRoot}>
                <Grid container className={classes.recipeCatListWrap}>
                    {recipeCategoryList.map((cat, index) => (
                        <Link to={cat.link} alt={`${cat.name}_${index}`} className={classes.recipeCat} key={seed(cat, index)}>{cat.name}</Link>
                    ))}
                </Grid>
            </div>
            <Grid container>
                {recipes && recipes.map((recipe, index) => (
                    <div role="presentation" onClick={(e) => { handleOnClick(e, recipe?.url, history); }} className={classes.recipeCatCont} key={seed(recipe, index)}>
                        <Grid container key={seed(recipe, index)} item md={12} lg={12} className={classes.recipeContentWrapper}>
                            <Grid item md={4} lg={3}>
                                <Grid item lg={12}>
                                    {recipe?.image_thumb?.url
                                        ? (<ResponsiveImage path={recipe?.image_thumb?.url} alt={recipe.title || ''} className={classes.recipeImg} params={{}} />)
                                        : (<ResponsiveImage path={brandLogo} alt={recipe.title || ''} className={classes.recipeImg} params={{}} />)}
                                </Grid>
                            </Grid>
                            <Grid item md={5} lg={6}>
                                <Grid item md={12} lg={12} className={classes.recipeName}>
                                    {recipe.title || ''}
                                </Grid>
                                <Grid item md={12} lg={12} className={classes.recipeShortDescription}>
                                    <ReactMarkdown source={recipe.short_description || ''} />
                                </Grid>
                            </Grid>
                            <Grid item md={3} lg={3} className={classes.viewBtnWrapper}>
                                <Button variant="contained" className={classes.viewBtn} aria-label={` View ${recipe?.title}`} alt={`${recipe?.title}_${index}`} href={recipe.url || ''}>
                                    View <ArrowRightIcon className={classes.arrowRightIcon} />
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                ))}
                {!recipes.length && <h3 className={classes.error}><em>There does not appear to be any recipes currently associated with this category.</em></h3>}
            </Grid>
        </>
    );
};

const mapStateToProps = (state) => ({
    brandLogo: getLogo(state),
});

RecipeCategoryPage.propTypes = {
    classes: object.isRequired,
    page: object.isRequired,
    recipes: arrayOf(
        shape({
            name: string,
            short_description: string,
        }),
    ).isRequired,
    recipeName: string.isRequired,
    brandLogo: string,
    seo: shape({
        page_title: string,
        meta_description: string,
        keywords: string,
        canonicalUrl: string,
    }).isRequired,
    brand: string.isRequired,
};

RecipeCategoryPage.defaultProps = {
    brandLogo: '',
};

export default withStyles(styles)(connect(mapStateToProps)(RecipeCategoryPage));
